// src/components/presets/BannerMeui.tsx
'use client'

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cookie, X, Check } from 'lucide-react'
import type { BannerProps } from '../Banner'
import { getAnimationValues, getBorderRadiusValue } from '../../utils/style-helpers'
import { getCombinedStyles } from '../../utils/positioning'
import { getCategoryLabel } from '../../utils/translation-helpers'

function CustomCheckbox({
  checked,
  disabled,
  onChange,
  label,
  sublabel,
  accentColor,
}: {
  checked: boolean
  disabled?: boolean
  onChange?: () => void
  label: string
  sublabel?: string
  accentColor: string
}) {
  const checkboxId = React.useId()

  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-start',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div style={{ position: 'relative', marginTop: '2px' }}>
        <input
          id={checkboxId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          style={{
            position: 'absolute',
            opacity: 0,
            width: '1px',
            height: '1px',
          }}
        />
        <label
          htmlFor={checkboxId}
          style={{
            display: 'block',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <div
            style={{
              width: '16px',
              height: '16px',
              borderRadius: '4px',
              border: checked ? `2px solid ${accentColor}` : '2px solid #d1d5db',
              backgroundColor: checked ? accentColor : '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
          >
            {checked && (
              <Check style={{ width: '12px', height: '12px', color: '#ffffff', strokeWidth: 3 }} />
            )}
          </div>
        </label>
      </div>
      <label
        htmlFor={checkboxId}
        style={{
          flex: 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <div style={{ fontSize: '12px', color: '#111827', fontWeight: 500 }}>
          {label}
          {sublabel && (
            <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: '4px' }}>{sublabel}</span>
          )}
        </div>
      </label>
    </div>
  )
}

export function BannerMeui(props: BannerProps) {
  const {
    config,
    texts,
    categories,
    consent,
    onAccept,
    onDecline,
    onClose,
    onConsentChange,
    accentColor,
    style,
  } = props
  const {
    closeButton,
    showSettingsButton,
    showRejectLink,
    descriptionMode = 'short',
    language = 'en',
  } = config
  const [showSettings, setShowSettings] = useState(false)

  const animationValues = getAnimationValues(style.animation.direction)
  const description =
    descriptionMode === 'long'
      ? texts.longDescription || texts.description
      : texts.shortDescription || texts.description

  // Override maxWidth for meui preset to make it narrower
  const meuiMaxWidth = (() => {
    const sizes: Record<string, string> = {
      sm: '320px',
      md: '360px',
      lg: '420px',
      xl: '480px',
    }
    return sizes[style.maxWidth] || '360px'
  })()

  const containerStyles = getCombinedStyles(style.position, meuiMaxWidth, {
    backgroundColor: style.background,
    borderRadius: getBorderRadiusValue(style.borderRadius),
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    border: '1px solid #e5e7eb',
  })

  const buttonBorderRadiusValue = style.borderRadius === 'sharp' ? '0' : '9999px'

  const handleSettingsClick = () => setShowSettings(!showSettings)
  const handleSaveSettings = () => {
    setShowSettings(false)
    onAccept()
  }

  return (
    <motion.div
      initial={style.animation.enabled ? animationValues.initial : { opacity: 1 }}
      animate={style.animation.enabled ? animationValues.animate : { opacity: 1 }}
      exit={style.animation.enabled ? animationValues.exit : { opacity: 0 }}
      transition={{ duration: style.animation.duration }}
      style={containerStyles}
    >
      <div style={{ position: 'relative' }}>
        {/* Decorative pattern */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0.03,
            pointerEvents: 'none',
          }}
        >
          <motion.div
            animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
            transition={{ duration: 20, repeat: Infinity, repeatType: 'reverse' }}
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `radial-gradient(circle at 3% 3%, black 1px, transparent 1px)`,
              backgroundSize: '30px 30px',
            }}
          />
        </div>

        <div style={{ position: 'relative', padding: '16px' }}>
          {closeButton && (
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              aria-label={texts.closeButton}
            >
              <X style={{ width: '14px', height: '14px', color: '#6b7280' }} />
            </button>
          )}

          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', paddingRight: '24px' }}>
            <div style={{ flexShrink: 0 }}>
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${accentColor}19`,
                }}
              >
                <Cookie style={{ width: '18px', height: '18px', color: accentColor }} />
              </div>
            </div>
            <div>
              <h3
                style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: '4px',
                  lineHeight: 1.25,
                }}
              >
                {texts.heading}
              </h3>
              <p
                style={{
                  fontSize: '12px',
                  color: '#4b5563',
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                {description}
              </p>
            </div>
          </div>

          <AnimatePresence>
            {showSettings && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ overflow: 'hidden' }}
              >
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '12px',
                    backgroundColor: 'rgba(249, 250, 251, 0.5)',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  {categories.map(category => (
                    <CustomCheckbox
                      key={category.id}
                      checked={consent[category.id] !== false}
                      disabled={category.required}
                      onChange={() =>
                        !category.required && onConsentChange?.(category.id, !consent[category.id])
                      }
                      label={getCategoryLabel(category, language)}
                      sublabel={category.required ? '(vždy aktivní)' : undefined}
                      accentColor={accentColor}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ display: 'flex', gap: '8px' }}>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={showSettings ? handleSaveSettings : onAccept}
              style={{
                flex: 1,
                padding: '8px 16px',
                fontSize: '12px',
                color: '#ffffff',
                backgroundColor: accentColor,
                border: 'none',
                borderRadius: buttonBorderRadiusValue,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
            >
              {showSettings ? texts.saveButton : texts.acceptButton}
            </motion.button>

            {showSettingsButton && (
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={handleSettingsClick}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  fontSize: '12px',
                  border: '1px solid #d1d5db',
                  color: '#374151',
                  backgroundColor: 'transparent',
                  borderRadius: buttonBorderRadiusValue,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = accentColor
                  e.currentTarget.style.color = accentColor
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '#d1d5db'
                  e.currentTarget.style.color = '#374151'
                }}
              >
                {showSettings ? texts.cancelButton : texts.settingsButton}
              </motion.button>
            )}
          </div>

          {showRejectLink && !showSettings && (
            <button
              onClick={onDecline}
              style={{
                width: '100%',
                textAlign: 'center',
                fontSize: '12px',
                color: '#6b7280',
                backgroundColor: 'transparent',
                border: 'none',
                padding: '8px',
                marginTop: '8px',
                cursor: 'pointer',
                transition: 'color 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = '#374151')}
              onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
            >
              {texts.rejectLink || texts.declineButton}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  )
}
